import { RowAction } from "@beeldit/core/enums/RowAction.enum";
import { TableAction } from "@beeldit/core/enums/TableAction.enum";
import { Check, Download, EnvelopeSimpleOpen, FileArrowDown, FilePlus, Files, HandCoins, Pencil, Plus, Prohibit, Trash } from "@phosphor-icons/react";
import { t } from "i18next";
import { Booking } from "./models/Booking";
import CustomerSelector from "@main/customers/components/CustomerSelector";
import PetCheckboxesSelector from "@main/pets/components/PetCheckboxesSelector";
import DaysInput from "./components/DaysInput";
import PricePerDaysInput from "./components/PricePerDaysInput";
import ExtraSelector from "@main/extras/components/ExtraSelector";
import RoomsSelector from "@main/rooms/components/RoomsSelector";
import TotalPriceInput from "./components/TotalPriceInput";
import PetSelector from "@main/pets/components/PetSelector";
import BeelditDateTimeInput from "@beeldit/core/components/BeelditDateTimeInput";
import RoomSelector from "@main/rooms/components/RoomSelector";
import CustomerWithModalSelector from "@main/customers/components/CustomerWithModalSelector";
import { GenerateDeliveryNoteActionContext } from "./actions/generate-delivery-note/GenerateDeliveryNoteActionContext";
import { GenerateInvoiceActionContext } from "./actions/generate-invoice/GenerateInvoiceActionContext";

const arrivalBookingsConfig = {
  modelName: "booking",
  modelPluralName: "bookings",
  endpoint: "bookings",
  indexQueryParams: {
    relations: ["customer", "rooms", "pets", "extras", "payment"],
    count_checked_in: true,
    appends: ['pick-up', 'invoice_amount']
  },
  modelClass: Booking,
  modalFullScreen: true,
  tableConfig: {
    index: null,
    actions: [
      {
        name: TableAction.CREATE,
        label: "create",
        showLabel: true,
        allowed: true,
        show: true,
        action: null,
        icon: <Plus size={20} />,
      },
      {
        name: 'download',
        label: "download",
        showLabel: true,
        allowed: true,
        show: true,
        action: null,
        icon: <Download size={20} />,
      },
    ],
    bulkActions: [
      {
        name: "create-delivery-note",
        label: "generate-delivery-note",
        context: GenerateDeliveryNoteActionContext,
        showLabel: true,
        allowed: true,
        show: true,
        action: null,
        icon: <Files size={20} />,
      },
      {
        name: "create-invoice",
        label: "create-invoice",
        context: GenerateInvoiceActionContext,
        showLabel: true,
        allowed: true,
        show: true,
        action: null,
        icon: <Files size={20} />,
      },
    ],
    rowActions: [
      {
        name: RowAction.EDIT,
        label: "edit",
        icon: <Pencil size={20} />,
        allowed: true,
        show: true,
        action: (element: any) => {
          console.log("edit", element);
        },
      },
      {
        name: "cancel",
        label: "cancel",
        icon: <Prohibit size={20} />,
        allowed: true,
        show: (element: any) => {
          return !element.cancelled;
        },
        action: (element: any) => {
          console.log("cancel", element);
        },        
      },
      {
        name: "uncancel",
        label: "uncancel",
        icon: <Check size={20} />,
        allowed: true,
        show: (element: any) => {
          return element.cancelled;
        },
        action: (element: any) => {
          console.log("uncancel", element);
        },        
      },
      {
        name: "billing",
        label: "billing",
        allowed: true,
        show: true,
        actions: [
          {
            name: "download-invoice-pdf",
            label: "download-invoice-pdf",
            icon: <FileArrowDown size={20} />,
            allowed: true,
            show: (element: any) => !!element.invoice_pdf_path,
            action: (invoicePdfPath: any) => {},
            data: "invoice_pdf_path",
          },
          {
            name: "generate-invoice-pdf",
            label: "generate-invoice-pdf",
            icon: <FilePlus size={20} />,
            allowed: true,
            show: (element: any) => {
              return !!element.invoice_id && !element.invoice_pdf_path;
            },
            action: (invoiceId: any) => {},
            data: "invoice_id",
          },
          {
            name: "regenerate-invoice-pdf",
            label: "regenerate-invoice-pdf",
            icon: <FilePlus size={20} />,
            allowed: true,
            show: (element: any) => {
              return !!element.invoice_id && !!element.invoice_pdf_path;
            },
            action: (invoiceId: any) => {},
            data: "invoice_id",
          },
          {
            name: "edit-invoice",
            label: "edit_invoice",
            icon: <Pencil size={20} />,
            allowed: true,
            show: (element: any) => !!element.invoice_id,
            action: (invoiceId: any) => {},
            data: "invoice_id",
          },
          {
            name: "replicate-invoice",
            label: "replicate_invoice",
            icon: <Files size={20} />,
            allowed: true,
            show: (element: any) => !!element.invoice_id,
            action: (invoiceId: any) => {},
            data: "invoice_id",
          },
          {
            name: "download-delivery-note-pdf",
            label: "download-delivery-note-pdf",
            icon: <FileArrowDown size={20} />,
            allowed: true,
            show: (element: any) => !!element.delivery_note_pdf_path,
            action: (deliveryNotePdfPath: any) => {},
            data: "delivery_note_pdf_path",
          },
          {
            name: "generate-delivery-note-pdf",
            label: "generate-delivery-note-pdf",
            icon: <FilePlus size={20} />,
            allowed: true,
            show: (element: any) => {
              return !!element.delivery_note_id && !element.delivery_note_pdf_path;
            },
            action: (deliveryNoteId: any) => {},
            data: "delivery_note_id",
          },
          {
            name: "edit-delivery-note",
            label: "edit_delivery_note",
            icon: <Pencil size={20} />,
            allowed: true,
            show: (element: any) => !!element.delivery_note_id,
            action: (deliveryNoteId: any) => {},
            data: "delivery_note_id",
          },
          {
            name: "replicate-delivery-note",
            label: "replicate_delivery_note",
            icon: <Files size={20} />,
            allowed: true,
            show: (element: any) => !!element.delivery_note_id,
            action: (deliveryNoteId: any) => {},
            data: "delivery_note_id",
          },
          {
            name: "pay",
            label: "pay",
            icon: <HandCoins size={20} />,
            allowed: true,
            show: (element: any) => {
              return !element.payment || !element.payment.payed;
            },
            action: (element: any) => {
              console.log("pay", element);
            },
            data: "payment.external_id",
          },
          {
            name: "unpay",
            label: "unpay",
            icon: <HandCoins size={20} />,
            allowed: true,
            show: (element: any) => {
              return element.payment && element.payment.payed;
            },
            action: (element: any) => {
              console.log("pay", element);
            },
            data: "payment.external_id",
          },
          {
            name: "email",
            label: "email",
            icon: <EnvelopeSimpleOpen size={20} />,
            allowed: true,
            show: true,
            action: (element: any) => {
              console.log("pay", element);
            },        
          },
          {
            name: "agreement",
            label: "agreement",
            icon: <Files size={20} />,
            allowed: true,
            show: (element: any) => {
              return !element.payment || !element.payment.payed;
            },
            action: (element: any) => {
              const host = process.env.REACT_APP_API_HOST;
              window.open(`${host}agreement/download/${element}`, "_blank");
            },
            data: "encrypted_id",
          },
        ],
      },
    ],
    rowConfiguration: {
      class: (element: Booking) => {
        if (!element.checked_in) {
          return "table-danger";
        } else {
          return "table-success";
        }
      },
    },
    columns: [
      {
        name: "id",
        label: "#",
        key: "id",
        type: "number",
        show: true,
        allowOrder: true,
      },
      {
        name: "cancelled",
        label: "cancelled",
        key: "cancelled",
        type: "boolean",
        show: true,
      },
      {
        name: "customer",
        label: "customer",
        key: "customer.full_name",
        type: "string",
        show: true,
        allowOrder: true,
      },
      {
        name: "start_date_time",
        label: "start_date_time",
        key: "start_date_time",
        type: "datetime",
        show: true,
        allowOrder: true,
      },
      {
        name: "end_date_time",
        label: "end_date_time",
        key: "end_date_time",
        type: "datetime",
        show: true,
        allowOrder: true,
      },
      {
        name: "rooms",
        label: "Hab",
        key: "rooms",
        attribute: "name",
        type: "array",
        show: true,
        allowOrder: true,
      },
      {
        name: "pets",
        label: "pets",
        key: "pets",
        attribute: "name",
        type: "array",
        show: true,
        allowOrder: true,
      },
      {
        name: "breeds",
        label: "breeds",
        key: "pets",
        attribute: "breed",
        type: "array",
        show: true,
        allowOrder: true,
      },
      {
        name: "to_pick_up",
        label: "to_pick_up",
        key: "pick-up",
        type: "boolean",
        show: true,
        allowOrder: true,
      },
      {
        name: "payed",
        label: "payed",
        key: "payment.payed",
        type: "boolean",
        show: true,
        allowOrder: true,
      },
      {
        name: "invoice",
        label: "invoice",
        key: "invoice_amount",
        type: "currency",
        currency: "EUR",
        show: true,
        allowOrder: false,
      },
    ],
    pagination: {
      enabled: true,
      back: true,
    },
  },
  filterConfig: {
    type: "object",
    properties: {
      search: {
        title: t("search_by_reference"),
        type: "string",
      },
      room_id: {
        title: t("room_id"),
        type: "number",
      },
      customer_id: {
        title: t("customer_id"),
        type: "number",
      },
      pet_id: {
        title: t("pet_id"),
        type: "number",
      },
      checked_in_from: {
        type: "string",
        format: "date",
        title: t("checked_in_from"),
        default: new Date().toISOString().split("T")[0],
      },
      checked_in_to: {
        type: "string",
        format: "date",
        title: t("checked_in_to"),
        default: new Date().toISOString().split("T")[0],
      },
      
    },
  },
  filterUIConfig: {
    uiScheme: {
      className: "filters-container",
      search: {
        "ui:options": {
          placeholder: "Buscar...",
        },
      },
      room_id: {
        "ui:widget": "room-selector",
      },
      customer_id: {
        "ui:widget": "customer-selector",
      },
      pet_id: {
        "ui:widget": "pet-selector",
      },
      // checked_in_from: {
      //   "ui:options": {
      //     label: false,
      //   },
      // },
      // checked_in_to: {
      //   "ui:options": {
      //     label: false,
      //   },
      // },
    },
    widgets: {
      "room-selector": RoomSelector,
      "customer-selector": CustomerSelector,
      "pet-selector": PetSelector,
    },
  },
  interactions: [
    {
      field: "pets",
      listen: "customer_id",
    },
  ],
  formSchema: {
    type: "object",
    properties: {
      // Reference
      reference: {
        type: "string",
        title: t("reference"),
        readonly: true,
      },
      // Customer
      customer_id: {
        type: "number",
        title: t("customer_id"),
      },
      // Pet
      pets: {
        type: "array",
        title: t("pets"),
        items: {
          type: "number",
        },
      },
      // Start date time
      start_date_time: {
        type: "string",
        format: "date-time",
        title: t("start_date_time"),
      },
      // End date time
      end_date_time: {
        type: "string",
        format: "date-time",
        title: t("end_date_time"),
      },
      // Rooms
      rooms: {
        type: "array",
        title: t("rooms"),
        items: {
          type: "number",
        },
      },
      // Days
      days: {
        type: "number",
        title: t("days"),
      },
      // Price per day
      price_per_day: {
        type: "number",
        title: t("price_per_day"),
        min: 0,
        step: 0.01,
        default: 0,
      },
      extras: {
        type: "array",
        title: t("extras"),
        items: {
          type: "object",
          properties: {
            extra_id: {
              type: "number",
              title: t("extra_id"),
            },
            price: {
              type: "number",
              title: t("extra_price"),
              min: 0,
              default: 0,
            },
            quantity: {
              type: "number",
              title: t("quantity"),
              min: 0,
              default: 1,
            },
          },
        },
      },
      total_price: {
        type: "number",
        title: t("total_price"),
        default: 0,
      },
      // Observation
      observation: {
        type: "string",
        title: t("observation"),
      },
    },
  },
  editFormSchema: {
    type: "object",
    properties: {
      // Reference
      reference: {
        type: "string",
        title: t("reference"),
        // readonly: true,
      },
      // Customer_indo
      customer_id: {
        type: "number",
        title: t("customer_id"),
      },

      // Pet
      pets: {
        type: "array",
        title: t("pets"),
        items: {
          type: "number",
        },
      },
      // Start date time
      start_date_time: {
        type: "string",
        format: "date-time",
        title: t("start_date_time"),
      },
      // End date time
      end_date_time: {
        type: "string",
        format: "date-time",
        title: t("end_date_time"),
      },
      // Rooms
      rooms: {
        type: "array",
        title: t("rooms"),
        items: {
          type: "number",
        },
      },
      // Days
      days: {
        type: "number",
        title: t("days"),
      },
      // Price per day
      price_per_day: {
        type: "number",
        title: t("price_per_day"),
        min: 0,
        step: 0.01,
        default: 0,
      },
      extras: {
        type: "array",
        title: t("extras"),
        items: {
          type: "object",
          properties: {
            extra_id: {
              type: "number",
              title: t("extra_id"),
            },
            price: {
              type: "number",
              title: t("extra_price"),
              min: 0,
              default: 0,
            },
            quantity: {
              type: "number",
              title: t("quantity"),
              min: 0,
              default: 1,
            },
          },
        },
      },
      checked_in: {
        type: "boolean",
        title: t("checked_in"),
        default: false,
      },
      checked_out: {
        type: "boolean",
        title: t("checked_out"),
        default: false,
      },
      total_price: {
        type: "number",
        title: t("total_price"),
        default: 0,
      },
      // Observation
      observation: {
        type: "string",
        title: t("observation"),
      },
    },
  },
  formWidgets: {
    "customer-selector": CustomerWithModalSelector,
    "pet-checkboxes-selector": PetCheckboxesSelector,
    days: DaysInput,
    "rooms-selector": RoomsSelector,
    "price-per-day": PricePerDaysInput,
    "extra-selector": ExtraSelector,
    "total-price-input": TotalPriceInput,
    "datetime-input": BeelditDateTimeInput,
  },
  uiFormSchema: {
    "ui:submitButtonOptions": {
      props: {
        disabled: false,
        className: "btn btn-info",
      },
      norender: true,
    },
    days: {
      "ui:widget": "days",
      "ui:options": {
        label: false,
      },
    },
    customer_id: {
      "ui:widget": "customer-selector",
    },
    pets: {
      "ui:widget": "pet-checkboxes-selector",
      "ui:options": {
        filters: {
          customer_id: "customer_id",
        },
      },
    },
    rooms: {
      "ui:widget": "rooms-selector",
    },
    price_per_day: {
      "ui:widget": "price-per-day",
    },
    total_price: {
      "ui:widget": "total-price-input",
    },
    observation: {
      "ui:widget": "textarea",
    },
    start_date_time: {
      "ui:widget": "datetime-input",
    },
    end_date_time: {
      "ui:widget": "datetime-input",
    },
    colsSchema: [
      // Parte izquierda con 4 columnas con customer y pet
      {
        cols: "col-4",
        elements: [
          [
            { name: "customer_id", cols: "col-12" },
            { name: "customer_info", cols: "col-12" },
            { name: "pets", cols: "col-12" },
          ],
        ],
      },
      {
        cols: "col-8",
        elements: [
          [
            { name: "start_date_time", cols: "col-3" },
            { name: "end_date_time", cols: "col-3" },
            { name: "days", cols: "col-3" },
          ],
          [
            { name: "rooms", cols: "col-3" },
            { name: "price_per_day", cols: "col-3" },
          ],
          [
            { name: "extras", cols: "col-12" },
            { name: "checked_in", cols: "col-3" },
            { name: "checked_out", cols: "col-3" },
            { name: "total_price", cols: "col-12" },
            { name: "observation", cols: "col-12" },
          ],
        ],
      },
    ],
    extras: {
      "ui:widget": "extra-selector",
      items: {
        colsSchema: [
          {
            cols: "col-12",
            elements: [
              [
                { name: "extra_id", cols: "col-4" },
                { name: "price", cols: "col-4" },
                { name: "quantity", cols: "col-4" },
              ],
            ],
          },
        ],
      },
    },
  },
};
export default arrivalBookingsConfig;
